import { BillingInfo, LensSDKException } from "lens-platform-sdk";
import { misc } from "@k8slens/lds-icons";
import { openUrl } from "src/utils/url";

import TrackedButton from "src/components/Button/TrackedButton";
import { useLensPlatformClient } from "src/hooks/useLensPlatformClient";
import { useParams } from "react-router-dom";
import { getUpdateBillingDetailUrl } from "src/services/billing/billingUrl";
import Notification from "src/components/Notification/Notification";
import { useState } from "react";
import styles from "./BillingDetails.module.css";

const { CreditCardIcon } = misc;

const formatValidity = (paymentMethod: BillingInfo["paymentMethod"]) =>
  paymentMethod.expYear && paymentMethod.expMonth
    ? `${paymentMethod.expMonth}/${paymentMethod.expYear?.toString().slice(2, 4)}`
    : "n/a";
const formatValidityLong = (paymentMethod: BillingInfo["paymentMethod"]) =>
  paymentMethod.expYear && paymentMethod.expMonth
    ? new Date(
        // We need to set type as number because it was set as Number in the SDK
        paymentMethod.expYear as number,
        (paymentMethod.expMonth as number) - 1,
      ).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: undefined,
      })
    : "";

export interface BillingProps {
  billingInfo?: BillingInfo;
  billingInfoUrl: string | undefined;
}

export const getCreditCardNumberDisplayString = ({ paymentMethod }: BillingInfo) => {
  if (paymentMethod?.lastFour) {
    return `${paymentMethod.cardType} ***${paymentMethod?.lastFour}`;
  }

  return `${paymentMethod.cardType} ${paymentMethod?.firstSix}***`;
};

export const getCreditCardAreaLabelText = ({ paymentMethod }: BillingInfo) => {
  let creditCardNumber = "";

  if (paymentMethod?.lastFour) {
    creditCardNumber = `ending with ${paymentMethod.lastFour}`;
  } else if (paymentMethod.firstSix) {
    creditCardNumber = `starting with ${paymentMethod.firstSix}`;
  }

  return `${paymentMethod.cardType} ${creditCardNumber}, expiring on ${formatValidityLong(paymentMethod)}.`;
};

export const Billing = ({ billingInfo, billingInfoUrl }: BillingProps) => {
  const lensPlatformClient = useLensPlatformClient();
  const { businessId } = useParams<{ businessId: string }>();
  const [error, setError] = useState<LensSDKException>();

  const updatePaymentMethod = () => {
    if (!billingInfoUrl) {
      lensPlatformClient.business
        .createBillingPageToken(businessId)
        .then(({ hostedLoginToken }) => {
          const updateBillingDetailUrl = getUpdateBillingDetailUrl(hostedLoginToken);

          openUrl(updateBillingDetailUrl, true);
        })
        .catch((error: any) => {
          setError(error);
        });

      return;
    }
    openUrl(billingInfoUrl, true);
  };

  return (
    <div>
      <div className={styles.billingInfo}>
        {billingInfo && (
          <>
            <CreditCardIcon className={styles.paymentType} size="xxl" />
            <div className={styles.details}>
              <div aria-label={getCreditCardAreaLabelText(billingInfo)}>
                <span aria-hidden>{getCreditCardNumberDisplayString(billingInfo)}</span>
              </div>
              <div aria-hidden>Valid until: {formatValidity(billingInfo.paymentMethod)}</div>
            </div>
          </>
        )}
        {!billingInfo && <div className={styles.noPaymentMethod}>No payment method defined</div>}
      </div>
      {error ? (
        <Notification
          level="error"
          message={error?.message ?? "unknown"}
          type="flash"
          className={styles.errorNotification}
        />
      ) : null}
      <TrackedButton
        label="Update payment method"
        onClick={updatePaymentMethod}
        className={styles.updatePaymentMethodButton}
      />
    </div>
  );
};
